import { useState, useCallback, useRef } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getActiveTenant } from '@/lib/active-tenant';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export interface FileRecord {
  id: number;
  url: string;
  name?: string;
}

interface FileUploadFieldProps {
  value: FileRecord | null;
  onChange: (file: FileRecord | null) => void;
  accept: string;
  placeholder?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  /** Эндпоинт загрузки относительно apiUrl. По умолчанию '/upload/book-file'. */
  endpoint?: string;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

const initialState: UploadState = { isUploading: false, progress: 0, error: null };

/**
 * Универсальная загрузка файлов через POST /upload/file.
 * Используется для source-файлов книг (EPUB/PDF/FB2) и аудио-файлов глав.
 */
export function FileUploadField({
  value,
  onChange,
  accept,
  placeholder = 'Выберите файл',
  maxSizeMB = 500,
  disabled = false,
  endpoint = '/upload/book-file',
}: FileUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const apiUrl = useApiUrl();
  const [state, setState] = useState<UploadState>(initialState);

  const uploadFile = useCallback(
    (file: File): Promise<FileRecord> =>
      new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}${endpoint}`);

        const token = localStorage.getItem('accessToken');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // X-Tenant-Id: XHR минует global fetch interceptor — добавляем вручную,
        // иначе на узб (club) upload падал в default-тенант.
        const tenant = getActiveTenant();
        if (tenant) {
          xhr.setRequestHeader('X-Tenant-Id', tenant);
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setState((prev) => ({ ...prev, progress: percent }));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success && response.data?.id && response.data?.url) {
                resolve({
                  id: response.data.id,
                  url: response.data.url,
                  name: file.name,
                });
              } else {
                reject(new Error(response.errors?.[0] ?? 'Ошибка загрузки'));
              }
            } catch {
              reject(new Error('Ошибка обработки ответа'));
            }
          } else {
            reject(new Error(`Ошибка загрузки: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Ошибка сети'));
        xhr.send(formData);
      }),
    [apiUrl, endpoint],
  );

  const handleSelect = useCallback(
    async (file: File) => {
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        setState({ ...initialState, error: `Размер не должен превышать ${maxSizeMB} МБ` });
        return;
      }

      setState({ isUploading: true, progress: 0, error: null });
      try {
        const record = await uploadFile(file);
        setState(initialState);
        onChange(record);
      } catch (err) {
        setState({
          isUploading: false,
          progress: 0,
          error: err instanceof Error ? err.message : 'Ошибка загрузки',
        });
      }
    },
    [uploadFile, onChange, maxSizeMB],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      void handleSelect(files[0]);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onInputChange}
        className="hidden"
        disabled={disabled}
      />

      {value ? (
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3">
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <a
            href={value.url}
            target="_blank"
            rel="noreferrer"
            className="flex-1 truncate text-sm hover:underline"
          >
            {value.name ?? value.url}
          </a>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || state.isUploading}
          >
            <Upload className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            disabled={disabled || state.isUploading}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || state.isUploading}
          className={cn(
            'w-full rounded-md border-2 border-dashed border-muted-foreground/25 p-6 text-center transition-colors',
            'hover:border-primary/50',
            disabled && 'opacity-50 cursor-not-allowed',
            state.error && 'border-destructive',
          )}
        >
          {state.isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <Progress value={state.progress} className="h-2 w-full max-w-xs" />
              <p className="text-sm text-muted-foreground">Загрузка... {state.progress}%</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{placeholder}</p>
              <p className="text-xs text-muted-foreground/70">Макс. {maxSizeMB} МБ</p>
            </div>
          )}
        </button>
      )}

      {state.error && <p className="text-sm text-destructive">{state.error}</p>}
    </div>
  );
}
